import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Menu,
  Progress,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import {
  BarChartOutlined,
  DashboardOutlined,
  FileSearchOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import Axios from 'axios';
import { useSelector } from 'react-redux';
import { TrendChart } from './Admin360Charts';
import Admin360OrganizationView from './Admin360OrganizationView';
import './Admin360Page.css';

const { Title, Text } = Typography;

const formatPercent = (value) =>
  Number.isFinite(Number(value)) ? Number(value) * 100 : 0;

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '—';
  return date.toLocaleDateString('ja-JP');
};

const statusMeta = {
  complete: { label: '回収完了', color: 'success' },
  scheduled: { label: '実施前', color: 'default' },
  closed_incomplete: { label: '未完了で終了', color: 'warning' },
  in_progress: { label: '実施中', color: 'processing' },
};

const roleSummary = (roleCounts = {}) =>
  ['self', 'boss', 'partner', 'sub']
    .map((key) => roleCounts[key])
    .filter((role) => role && role.expected > 0)
    .map((role) => `${role.label}${role.answered}/${role.expected}`)
    .join('・');

const AdminFeatureLink = ({
  title,
  description,
  href,
  buttonLabel,
  onClick,
  disabled = false,
}) => (
  <Card size="small" className="admin360__feature-link">
    <div>
      <b>{title}</b>
      <Text type="secondary">{description}</Text>
    </div>
    <Button href={href} onClick={onClick} disabled={disabled}>
      {buttonLabel}
    </Button>
  </Card>
);

const Admin360Page = () => {
  const currentUser = useSelector((state) => state.user.userData);
  const [view, setView] = useState('dashboard');
  const [surveyTab, setSurveyTab] = useState('overview');
  const [personQuery, setPersonQuery] = useState('');
  const [personDepartment, setPersonDepartment] = useState('all');
  const [personPosition, setPersonPosition] = useState('all');
  const [personResultStatus, setPersonResultStatus] = useState('all');
  const [groups, setGroups] = useState([]);
  const [groupCode, setGroupCode] = useState();
  const [dashboard, setDashboard] = useState(null);
  const [selectedSeasonId, setSelectedSeasonId] = useState();
  const [subjectData, setSubjectData] = useState(null);
  const [organizationData, setOrganizationData] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingOrganization, setLoadingOrganization] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const loadGroups = async () => {
      try {
        setLoadingGroups(true);
        const response = await Axios.get('/api/admin360/groups');
        if (!active) return;
        const loadedGroups = response.data.groups || [];
        setGroups(loadedGroups);
        const ownGroup = loadedGroups.find(
          (group) => group.code === currentUser?.groupCode
        );
        setGroupCode((ownGroup || loadedGroups[0] || {}).code);
      } catch (requestError) {
        if (active) {
          setError(
            requestError.response?.data?.error ||
              '企業一覧を取得できませんでした。'
          );
        }
      } finally {
        if (active) setLoadingGroups(false);
      }
    };
    loadGroups();
    return () => {
      active = false;
    };
  }, [currentUser]);

  useEffect(() => {
    if (!groupCode) {
      setDashboard(null);
      return undefined;
    }
    let active = true;
    const loadDashboard = async () => {
      try {
        setLoadingDashboard(true);
        setError('');
        const response = await Axios.get('/api/admin360/dashboard', {
          params: { groupCode },
        });
        if (!active) return;
        setDashboard(response.data);
        setSelectedSeasonId((current) =>
          response.data.seasons?.some((season) => season.id === current)
            ? current
            : response.data.seasons?.[0]?.id
        );
        setSubjectData(null);
        setOrganizationData(null);
      } catch (requestError) {
        if (active) {
          setDashboard(null);
          setError(
            requestError.response?.data?.error ||
              '360度評価の集計を取得できませんでした。'
          );
        }
      } finally {
        if (active) setLoadingDashboard(false);
      }
    };
    loadDashboard();
    return () => {
      active = false;
    };
  }, [groupCode]);

  useEffect(() => {
    if (!['surveys', 'person'].includes(view) || !selectedSeasonId) {
      return undefined;
    }
    let active = true;
    const loadSubjects = async () => {
      try {
        setLoadingSubjects(true);
        setError('');
        const response = await Axios.get(
          `/api/admin360/seasons/${encodeURIComponent(
            selectedSeasonId
          )}/subjects`
        );
        if (active) setSubjectData(response.data);
      } catch (requestError) {
        if (!active) return;
        setSubjectData(null);
        setError(
          requestError.response?.data?.error ||
            '対象者一覧を取得できませんでした。'
        );
      } finally {
        if (active) setLoadingSubjects(false);
      }
    };
    loadSubjects();
    return () => {
      active = false;
    };
  }, [selectedSeasonId, view]);

  useEffect(() => {
    if (view !== 'org' || !selectedSeasonId) return undefined;
    let active = true;
    const loadOrganization = async () => {
      try {
        setLoadingOrganization(true);
        setError('');
        const response = await Axios.get(
          `/api/admin360/seasons/${encodeURIComponent(
            selectedSeasonId
          )}/organization`
        );
        if (active) setOrganizationData(response.data);
      } catch (requestError) {
        if (!active) return;
        setOrganizationData(null);
        setError(
          requestError.response?.data?.error ||
            '組織レポートを取得できませんでした。'
        );
      } finally {
        if (active) setLoadingOrganization(false);
      }
    };
    loadOrganization();
    return () => {
      active = false;
    };
  }, [selectedSeasonId, view]);

  useEffect(() => {
    setPersonQuery('');
    setPersonDepartment('all');
    setPersonPosition('all');
    setPersonResultStatus('all');
  }, [groupCode, selectedSeasonId]);

  const selectedGroup = groups.find((group) => group.code === groupCode);
  const seasons = dashboard?.seasons || [];
  const totals = dashboard?.totals;
  const selectedSeason = seasons.find(
    (season) => season.id === selectedSeasonId
  );

  const seasonColumns = useMemo(
    () => [
      {
        title: '実施回',
        dataIndex: 'name',
        key: 'name',
        width: 250,
        render: (name, record) => (
          <div className="admin360__season-name">
            <b>{name}</b>
            <span>
              {record.surveyCode} / {record.type360}
            </span>
          </div>
        ),
      },
      {
        title: '提出期間',
        key: 'period',
        width: 190,
        render: (_, record) => {
          const start = record.submittedPeriod?.start;
          const end = record.submittedPeriod?.end;
          return start ? (
            <span>
              {formatDate(start)} 〜 {formatDate(end)}
            </span>
          ) : (
            <Text type="secondary">提出なし</Text>
          );
        },
      },
      {
        title: '対象者',
        dataIndex: 'targetCount',
        key: 'targetCount',
        align: 'center',
        width: 90,
        render: (value) => `${value}名`,
      },
      {
        title: '役割別 回収',
        dataIndex: 'roleCounts',
        key: 'roleCounts',
        width: 240,
        render: (value) => (
          <span className="admin360__role-summary">{roleSummary(value)}</span>
        ),
      },
      {
        title: '完了率',
        key: 'progress',
        width: 230,
        render: (_, record) => (
          <div className="admin360__progress">
            <Progress
              percent={Number(formatPercent(record.completionRate).toFixed(1))}
              size="small"
              status={record.status === 'complete' ? 'success' : 'active'}
            />
            <small>
              {record.answeredCount} / {record.expectedCount}件
            </small>
          </div>
        ),
      },
      {
        title: '状態',
        dataIndex: 'status',
        key: 'status',
        align: 'center',
        width: 110,
        render: (value) => {
          const meta = statusMeta[value] || statusMeta.in_progress;
          return <Tag color={meta.color}>{meta.label}</Tag>;
        },
      },
      {
        title: '',
        key: 'action',
        align: 'center',
        width: 205,
        render: (_, record) => (
          <Space size="small">
            <Button
              size="small"
              onClick={() => {
                setSelectedSeasonId(record.id);
                setView('org');
              }}
            >
              組織レポート
            </Button>
            <Button
              size="small"
              onClick={() => {
                setSelectedSeasonId(record.id);
                setSurveyTab('overview');
                setView('surveys');
              }}
            >
              詳細
            </Button>
          </Space>
        ),
      },
    ],
    []
  );

  const subjectColumns = useMemo(() => {
    const surveyCode = subjectData?.season?.surveyCode;
    return [
      {
        title: '氏名',
        dataIndex: 'name',
        key: 'name',
        width: 150,
        render: (name, record) => (
          <div className="admin360__person">
            <b>{name}</b>
            <span>{record.email}</span>
          </div>
        ),
      },
      {
        title: '所属',
        dataIndex: 'department',
        key: 'department',
        width: 260,
        render: (departments) => (departments || []).join(' 〉 ') || '—',
      },
      {
        title: '職位',
        dataIndex: 'position',
        key: 'position',
        width: 100,
        render: (value) => value || '—',
      },
      {
        title: '役割別 回収',
        dataIndex: 'roleCounts',
        key: 'roleCounts',
        width: 250,
        render: (value) => (
          <span className="admin360__role-summary">{roleSummary(value)}</span>
        ),
      },
      {
        title: '回収',
        key: 'completion',
        align: 'center',
        width: 120,
        render: (_, record) => (
          <Tag color={record.complete ? 'success' : 'processing'}>
            {record.answeredCount}/{record.expectedCount}件
          </Tag>
        ),
      },
      {
        title: '結果',
        key: 'result',
        align: 'center',
        width: 120,
        render: (_, record) => (
          <Button
            type="primary"
            size="small"
            href={
              record.subjectTokenId
                ? `/results/${surveyCode}/${encodeURIComponent(
                    record.subjectTokenId
                  )}?view=admin&from=admin360`
                : undefined
            }
            target="_blank"
            rel="noopener noreferrer"
            disabled={!record.resultAvailable}
          >
            Webで見る
          </Button>
        ),
      },
    ];
  }, [subjectData]);

  const respondentColumns = useMemo(
    () =>
      subjectColumns.filter(
        (column) => !['completion', 'result'].includes(column.key)
      ),
    [subjectColumns]
  );

  const collectionColumns = useMemo(
    () => subjectColumns.filter((column) => column.key !== 'result'),
    [subjectColumns]
  );

  const personFilterOptions = useMemo(() => {
    const subjects = subjectData?.subjects || [];
    const departments = [
      ...new Set(subjects.flatMap((subject) => subject.department || [])),
    ].sort((left, right) => left.localeCompare(right, 'ja'));
    const positions = [
      ...new Set(subjects.map((subject) => subject.position).filter(Boolean)),
    ].sort((left, right) => left.localeCompare(right, 'ja'));
    return { departments, positions };
  }, [subjectData]);

  const filteredPersonalSubjects = useMemo(() => {
    const normalizedQuery = personQuery.trim().toLocaleLowerCase('ja');
    return (subjectData?.subjects || []).filter((subject) => {
      const searchable = [
        subject.name,
        subject.email,
        subject.position,
        ...(subject.department || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('ja');
      if (normalizedQuery && !searchable.includes(normalizedQuery)) return false;
      if (
        personDepartment !== 'all' &&
        !(subject.department || []).includes(personDepartment)
      ) {
        return false;
      }
      if (
        personPosition !== 'all' &&
        subject.position !== personPosition
      ) {
        return false;
      }
      if (
        personResultStatus === 'available' &&
        !subject.resultAvailable
      ) {
        return false;
      }
      if (
        personResultStatus === 'pending' &&
        subject.resultAvailable
      ) {
        return false;
      }
      return true;
    });
  }, [
    personDepartment,
    personPosition,
    personQuery,
    personResultStatus,
    subjectData,
  ]);

  const dashboardView = (
    <>
      <div className="admin360__heading">
        <div>
          <Title level={2}>ダッシュボード</Title>
          <Text type="secondary">
            全実施回を横断した360度評価・FS診断の回収状況
          </Text>
        </div>
      </div>
      {totals && (
        <Row gutter={[16, 16]} className="admin360__kpis">
          <Col xs={24} sm={12} xl={5}>
            <Card><Statistic title="サーベイ実施回" value={totals.seasonCount} suffix="回" /></Card>
          </Col>
          <Col xs={24} sm={12} xl={5}>
            <Card><Statistic title="対象者数（延べ）" value={totals.targetCount} suffix="名" /></Card>
          </Col>
          <Col xs={24} sm={12} xl={5}>
            <Card
              ><Statistic
                title="回答完了率（全体）"
                value={formatPercent(totals.completionRate)}
                precision={1}
                suffix="%"
              /></Card>
          </Col>
          <Col xs={24} sm={12} xl={5}>
            <Card
              ><Statistic
                title="回答完了数 / 依頼数"
                value={totals.answeredCount}
                suffix={`/ ${totals.expectedCount}件`}
              /></Card>
          </Col>
          <Col xs={24} sm={12} xl={4}>
            <Card
              ><Statistic
                title="平均協力者数"
                value={totals.averageCollaboratorCount}
                precision={1}
                suffix="名"
              /></Card>
          </Col>
        </Row>
      )}
      <Card title="サーベイ一覧" className="admin360__card">
        <Table
          rowKey="id"
          columns={seasonColumns}
          dataSource={seasons}
          pagination={false}
          scroll={{ x: 1240 }}
          locale={{ emptyText: '対象の実施回がありません' }}
        />
      </Card>
      <Row gutter={[16, 16]}>
        {seasons.map((season) => (
          <Col xs={24} xl={12} key={season.id}>
            <Card
              title={`回収推移 — ${season.name}`}
              className="admin360__card admin360__trend-card"
              extra={
                <Text type="secondary">
                  {season.answeredCount}/{season.expectedCount}件
                </Text>
              }
            >
              <Text type="secondary">
                提出日時ベースの累積回答数（100%＝依頼{season.expectedCount}件）
              </Text>
              <TrendChart
                trend={season.trend}
                expectedCount={season.expectedCount}
              />
            </Card>
          </Col>
        ))}
      </Row>
      <Card title="アラート・通知" className="admin360__card">
        {seasons.every(
          (season) => season.answeredCount >= season.expectedCount
        ) ? (
          <Alert
            type="success"
            showIcon
            message="対応が必要な未回答はありません"
          />
        ) : (
          <div className="admin360__notices">
            {seasons
              .filter(
                (season) => season.answeredCount < season.expectedCount
              )
              .map((season) => (
                <div className="admin360__notice" key={season.id}>
                  <div>
                    <b>{season.name}</b>
                    <span>
                      未回答 {season.expectedCount - season.answeredCount}件
                    </span>
                  </div>
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedSeasonId(season.id);
                      setSurveyTab('collection');
                      setView('surveys');
                    }}
                  >
                    対象者を確認
                  </Button>
                </div>
              ))}
          </div>
        )}
      </Card>
      <Card
        title="クイックアクション"
        extra={
          <Text type="secondary">
            {selectedSeason?.name || '実施回を選択してください'}
          </Text>
        }
        className="admin360__card"
      >
        <Text type="secondary">よく使う既存の管理機能へのショートカット</Text>
        <div className="admin360__feature-grid admin360__quick-actions">
          <AdminFeatureLink
            title="回答者設定"
            description="選択中の実施回の対象者・回答者を確認"
            buttonLabel="設定を確認"
            disabled={!selectedSeason}
            onClick={() => {
              setSurveyTab('respondents');
              setView('surveys');
            }}
          />
          <AdminFeatureLink
            title="組織レポート"
            description="選択中の実施回の全体傾向・部署別を分析"
            buttonLabel="レポートを見る"
            disabled={!selectedSeason}
            onClick={() => setView('org')}
          />
          <AdminFeatureLink
            title="個人結果"
            description="選択中の実施回の対象者別結果を確認"
            buttonLabel="対象者を選ぶ"
            disabled={!selectedSeason}
            onClick={() => setView('person')}
          />
          <AdminFeatureLink
            title="RawData書き出し"
            description="既存のCSVダウンロード機能を利用"
            href="/csvdownload"
            buttonLabel="書き出しを開く"
          />
        </div>
      </Card>
    </>
  );

  const personalResultsView = (
    <>
      <div className="admin360__heading">
        <div>
          <Title level={2}>個人結果（代行閲覧）</Title>
          <Text type="secondary">
            実施回を選択して対象者本人のWeb結果を確認します
          </Text>
        </div>
        <Select
          value={selectedSeasonId}
          onChange={setSelectedSeasonId}
          className="admin360__season-select"
          options={seasons.map((season) => ({
            value: season.id,
            label: `${season.name}（${season.type360}）`,
          }))}
          placeholder="実施回を選択"
        />
      </div>
      {loadingSubjects ? (
        <div className="admin360__loading"><Spin size="large" /></div>
      ) : subjectData ? (
        <Card
          title={subjectData.season.name}
          className="admin360__card"
        >
          <div className="admin360__person-filters">
            <Input.Search
              allowClear
              value={personQuery}
              onChange={(event) => setPersonQuery(event.target.value)}
              placeholder="氏名・所属・メールで検索"
            />
            <Select
              value={personDepartment}
              onChange={setPersonDepartment}
              options={[
                { value: 'all', label: 'すべての所属' },
                ...personFilterOptions.departments.map((department) => ({
                  value: department,
                  label: department,
                })),
              ]}
            />
            <Select
              value={personPosition}
              onChange={setPersonPosition}
              options={[
                { value: 'all', label: 'すべての職位' },
                ...personFilterOptions.positions.map((position) => ({
                  value: position,
                  label: position,
                })),
              ]}
            />
            <Select
              value={personResultStatus}
              onChange={setPersonResultStatus}
              options={[
                { value: 'all', label: 'すべての結果状態' },
                { value: 'available', label: 'Web閲覧可' },
                { value: 'pending', label: '結果準備中' },
              ]}
            />
            <Text type="secondary" className="admin360__person-filter-count">
              {filteredPersonalSubjects.length} /{' '}
              {(subjectData.subjects || []).length}名
            </Text>
          </div>
          <Table
            rowKey="id"
            columns={subjectColumns}
            dataSource={filteredPersonalSubjects}
            pagination={{ pageSize: 20, showSizeChanger: false }}
            scroll={{ x: 1120 }}
            locale={{ emptyText: '対象者が登録されていません' }}
          />
        </Card>
      ) : (
        <Empty description="実施回を選択してください" />
      )}
    </>
  );

  const surveyOverview = subjectData && selectedSeason ? (
    <>
      <Row gutter={[16, 16]} className="admin360__kpis">
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic
              title="対象者"
              value={selectedSeason.targetCount}
              suffix="名"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic
              title="回答完了率"
              value={formatPercent(selectedSeason.completionRate)}
              precision={1}
              suffix="%"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic
              title="回答完了数 / 依頼数"
              value={selectedSeason.answeredCount}
              suffix={`/ ${selectedSeason.expectedCount}件`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic
              title="平均協力者数"
              value={selectedSeason.averageCollaboratorCount}
              precision={1}
              suffix="名"
            />
          </Card>
        </Col>
      </Row>
      <Card title="実施回情報" className="admin360__card">
        <div className="admin360__season-overview">
          <div>
            <Text type="secondary">診断</Text>
            <b>
              {selectedSeason.surveyCode} / {selectedSeason.type360}
            </b>
          </div>
          <div>
            <Text type="secondary">実施期間</Text>
            <b>
              {formatDate(selectedSeason.period?.start)} 〜{' '}
              {formatDate(selectedSeason.period?.end)}
            </b>
          </div>
          <div>
            <Text type="secondary">役割別 回収</Text>
            <b>{roleSummary(selectedSeason.roleCounts) || '—'}</b>
          </div>
          <div>
            <Text type="secondary">状態</Text>
            <span>
              <Tag
                color={
                  (statusMeta[selectedSeason.status] || statusMeta.in_progress)
                    .color
                }
              >
                {(statusMeta[selectedSeason.status] || statusMeta.in_progress)
                  .label}
              </Tag>
            </span>
          </div>
        </div>
        {subjectData.season.description && (
          <div className="admin360__season-description">
            <Text type="secondary">説明</Text>
            <p>{subjectData.season.description}</p>
          </div>
        )}
        <Space wrap>
          <Button href="/seasonlist">実施回管理を開く</Button>
          <Button onClick={() => setSurveyTab('respondents')}>
            回答者設定へ
          </Button>
          <Button type="primary" onClick={() => setSurveyTab('collection')}>
            配信・回収を確認
          </Button>
        </Space>
      </Card>
    </>
  ) : null;

  const surveyRespondents = subjectData ? (
    <>
      <Alert
        type="info"
        showIcon
        message="対象者・回答者の変更は既存の管理機能で行います"
        description="アドミでは設定状況を確認し、既存画面へ移動して受講者、関係情報、チケットを管理します。"
        className="admin360__card"
      />
      <div className="admin360__feature-grid">
        <AdminFeatureLink
          title="受講者管理"
          description="対象者として利用する受講者情報を管理"
          href="/respondentmgr"
          buttonLabel="受講者を管理"
        />
        <AdminFeatureLink
          title="360度回答状況"
          description="対象者・回答者の関係と回答状況を確認"
          href="/status360"
          buttonLabel="回答状況を開く"
        />
        <AdminFeatureLink
          title="関係情報 CSV"
          description="対象者と回答者の関係情報を一括登録"
          href="/csvrelationupload"
          buttonLabel="CSV登録を開く"
        />
        <AdminFeatureLink
          title="サーベイチケット"
          description="回答用チケットをCSVで一括発行"
          href="/csvtokeninsert"
          buttonLabel="チケット発行を開く"
        />
      </div>
      <Card title="現在の対象者・回答者設定" className="admin360__card">
        <Table
          rowKey="id"
          columns={respondentColumns}
          dataSource={subjectData.subjects || []}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 760 }}
          locale={{ emptyText: '対象者が登録されていません' }}
        />
      </Card>
    </>
  ) : null;

  const surveyCollection = subjectData && selectedSeason ? (
    <>
      <Row gutter={[16, 16]} className="admin360__kpis">
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="回答完了率"
              value={formatPercent(selectedSeason.completionRate)}
              precision={1}
              suffix="%"
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="回答完了"
              value={selectedSeason.answeredCount}
              suffix="件"
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="未回答"
              value={Math.max(
                selectedSeason.expectedCount - selectedSeason.answeredCount,
                0
              )}
              suffix="件"
            />
          </Card>
        </Col>
      </Row>
      <Card title="案内・リマインド" className="admin360__card">
        <Space wrap>
          <Button href="/surveymailsend" type="primary">
            サーベイ案内メール送信
          </Button>
          <Button href="/mailsendreservate">メール送信予約</Button>
          <Button href="/mailreservatelist">送信予約を管理</Button>
          <Button href="/status360">既存の回答状況を開く</Button>
        </Space>
      </Card>
      <Card title="対象者別 回収状況" className="admin360__card">
        <Table
          rowKey="id"
          columns={collectionColumns}
          dataSource={subjectData.subjects || []}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 1000 }}
          locale={{ emptyText: '対象者が登録されていません' }}
        />
      </Card>
    </>
  ) : null;

  const surveyResults = subjectData ? (
    <>
      <Card className="admin360__card">
        <Space wrap>
          <Button
            type="primary"
            onClick={() => setView('org')}
          >
            組織Webレポートを見る
          </Button>
          <Button
            href={
              subjectData.season.surveyCode === '60020'
                ? '/download60020_SS'
                : '/download40010_SS'
            }
          >
            組織結果PDF
          </Button>
        </Space>
      </Card>
      <Card title="個人結果" className="admin360__card">
        <Table
          rowKey="id"
          columns={subjectColumns}
          dataSource={subjectData.subjects || []}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 1120 }}
          locale={{ emptyText: '対象者が登録されていません' }}
        />
      </Card>
    </>
  ) : null;

  const surveyManagementView = (
    <>
      <div className="admin360__heading">
        <div>
          <Title level={2}>サーベイ管理</Title>
          <Text type="secondary">
            実施回ごとの設定、配信・回収、結果を一か所で確認します
          </Text>
        </div>
        <Select
          value={selectedSeasonId}
          onChange={setSelectedSeasonId}
          className="admin360__season-select"
          options={seasons.map((season) => ({
            value: season.id,
            label: `${season.name}（${season.type360}）`,
          }))}
          placeholder="実施回を選択"
        />
      </div>
      {loadingSubjects ? (
        <div className="admin360__loading"><Spin size="large" /></div>
      ) : subjectData ? (
        <Card
          title={subjectData.season.name}
          className="admin360__card admin360__survey-management"
        >
          <Tabs
            activeKey={surveyTab}
            onChange={setSurveyTab}
            items={[
              { key: 'overview', label: '概要', children: surveyOverview },
              {
                key: 'respondents',
                label: '回答者設定',
                children: surveyRespondents,
              },
              {
                key: 'collection',
                label: '配信・回収',
                children: surveyCollection,
              },
              { key: 'results', label: '結果', children: surveyResults },
            ]}
          />
        </Card>
      ) : (
        <Empty description="実施回を選択してください" />
      )}
    </>
  );

  if (loadingGroups) {
    return <div className="admin360__loading"><Spin size="large" tip="アドミを読み込んでいます..." /></div>;
  }

  return (
    <div className="admin360">
      <div className="admin360__topbar">
        <div>
          <Title level={1}>360度評価 アドミ</Title>
          <Text type="secondary">人事・企画部門向け管理画面（role 9）</Text>
        </div>
        <div className="admin360__group-select">
          <label htmlFor="admin360-group">企業</label>
          <Select
            id="admin360-group"
            showSearch
            optionFilterProp="label"
            value={groupCode}
            onChange={setGroupCode}
            options={groups.map((group) => ({
              value: group.code,
              label: group.name,
            }))}
            placeholder="企業を選択"
          />
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError('')}
          className="admin360__alert"
        />
      )}

      {!groups.length ? (
        <Empty description="360度評価またはFS診断の企業データがありません" />
      ) : (
        <div className="admin360__layout">
          <aside>
            <div className="admin360__aside-title">360度評価 管理</div>
            <Menu
              mode="inline"
              selectedKeys={[view]}
              onClick={({ key }) => {
                if (key === 'surveys' && view !== 'surveys') {
                  setSurveyTab('overview');
                }
                setView(key);
              }}
              items={[
                { key: 'dashboard', icon: <DashboardOutlined />, label: 'ダッシュボード' },
                { key: 'surveys', icon: <FileSearchOutlined />, label: 'サーベイ管理' },
                { key: 'org', icon: <BarChartOutlined />, label: 'レポート: 組織' },
                { key: 'person', icon: <TeamOutlined />, label: 'レポート: 個人結果' },
              ]}
            />
            <div className="admin360__aside-group">
              表示中<br /><b>{selectedGroup?.name}</b>
            </div>
          </aside>
          <main>
            {loadingDashboard ? (
              <div className="admin360__loading"><Spin size="large" /></div>
            ) : view === 'dashboard' ? (
              dashboardView
            ) : view === 'org' ? (
              loadingOrganization ? (
                <div className="admin360__loading"><Spin size="large" /></div>
              ) : organizationData ? (
                <Admin360OrganizationView
                  data={organizationData}
                  selectedSeasonId={selectedSeasonId}
                  onSeasonChange={setSelectedSeasonId}
                  seasonOptions={seasons.map((season) => ({
                    value: season.id,
                    label: `${season.name}（${season.type360}）`,
                  }))}
                />
              ) : (
                <Empty description="実施回を選択してください" />
              )
            ) : view === 'surveys' ? (
              surveyManagementView
            ) : (
              personalResultsView
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default Admin360Page;
